import React, { useEffect, useMemo, useState } from 'react';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce, sortBy } from 'lodash-es';
import { css } from 'styled-components';

import { trackEvent } from 'jf/analytics/Analytics';
import { MsTeamsTargetingClient } from 'jf/api';
import { DevExEmpty } from 'jf/components/DevExEmpty';
import { DevExLoader } from 'jf/components/DevExLoader';
import { DevExSelect } from 'jf/components/DevExSelect';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { MSTeamsEntityDisplay } from './MSTeamsEntityDisplay';

const styles = {
  select: css`
    &&&&.ant-select {
      min-width: 520px;
      &.ant-select-lg {
        .ant-select-selector {
          border-radius: 5px;
        }
      }
    }
  `,
};

type MSTeamsSearchBarProps = {
  setSelectedMsteamsUsers: any;
  expandMsteamsEntity: any;
};

export const MSTeamsSearchBar: React.FC<MSTeamsSearchBarProps> = ({
  setSelectedMsteamsUsers,
  expandMsteamsEntity,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const { data: msteamsEntities, isFetching } = useClientQuery(
    MsTeamsTargetingClient.getMsteamsEntities,
    { filter: debouncedSearchQuery },
    { enabled: debouncedSearchQuery.length >= 1 }
  );

  const debounceFiredSearchQuery = useMemo(() => debounce(setDebouncedSearchQuery, 300), []);
  useEffect(() => {
    if (searchQuery) {
      debounceFiredSearchQuery(searchQuery);
    } else {
      debounceFiredSearchQuery.cancel();
      setDebouncedSearchQuery('');
    }
  }, [searchQuery]);

  const isSearching = searchQuery !== debouncedSearchQuery || isFetching;

  const msteamsEntityOptions = useMemo(() => {
    return sortBy(msteamsEntities, (entity) => entity.name.toLowerCase()).map((entity) => ({
      value: entity.id,
      label: (
        <MSTeamsEntityDisplay
          msteamsUser={{
            id: entity.id,
            email: entity.email || '',
            displayName: entity.displayName || entity.name || '',
            name: entity.name,
            image: entity.image || [],
          }}
          entityType={entity.type}
          previewVersion
        />
      ),
    }));
  }, [msteamsEntities]);

  return (
    <DevExSelect
      css={styles.select}
      showSearch
      filterOption={() => true} // leave filtering to the BE only
      options={!isSearching ? msteamsEntityOptions : undefined}
      value={[]}
      onChange={(value) => {
        const entity = msteamsEntities?.find((entity) => entity.id === value);
        if (entity) {
          expandMsteamsEntity({
            requestBody: {
              entityId: entity.id,
              entityType: entity.type,
            },
          }).then((newMsteamsUsers) => {
            setSelectedMsteamsUsers((users) => {
              return [
                ...users,
                // filter out duplicates before adding
                ...newMsteamsUsers.filter(
                  (newTarget) => !users.some((target) => target.id === newTarget.id)
                ),
              ];
            });
            trackEvent('survey-editor:msteams-target-drawer:expand-msteams-entity', {
              msteamsUserId: entity.id,
              entityType: entity.type,
            });
          });
        }
        setSearchQuery('');
      }}
      placeholder="Search for @users and @groups"
      loading={isSearching}
      suffixIcon={<FontAwesomeIcon icon={faSearch} />}
      notFoundContent={
        isSearching ? (
          <DevExLoader tip="Searching MS Teams..." />
        ) : (
          <DevExEmpty
            icon={faMicrosoft}
            label={
              searchQuery
                ? `No msteams targets found matching "${searchQuery}".`
                : 'Search for @users and @groups in your MS Teams instance.'
            }
          />
        )
      }
      onSearch={setSearchQuery}
    />
  );
};
